import { useCallback, useContext, useState } from "react";

import { useProductData2 } from "../context/products";
import { CartContext } from "../context/CartProvider";
import { useAddToCartWithLuxe } from "../context/LuxeProvider";
import StoreContext from "../context/store";
import { isOutOfStock } from "../context/helpers";
import { useTracking } from "../context/Tracking";

import { useCurrentCountryCode, useCurrentCurrencyCode } from "../hooks/usePrices";
import useDiscountsInfo from "../hooks/useDiscountsInfo";

import { useALError } from "../helpers/ErrorBoundary/ALErrorBoundary";

import { ComponentType } from "../constants/ComponentType";
import { ALButton, ALLoading } from "./ALComponents";
import ProductElement from "./ProductElement";
import TAG from "../constants/Tag";
import { Country } from "../constants/Country";
import IconCloseRound from "./icons/svgs/close-round.svg";

import "./productRecommendationsPopup/styles.scss";

function ProductRecommendationsPopup() {
  const { isSideCartOpen } = useContext(CartContext);
  const { recommendedProductsPopupData, setRecommendedProductsPopupData } =
    useContext(StoreContext);

  const countryCode = useCurrentCountryCode();
  const currencyCode = useCurrentCurrencyCode();
  const discountsInfo = useDiscountsInfo();

  const { trackRecommendedProductsPopupAction, trackRecommendedProductsATC } = useTracking();
  const addToCartWithLuxe = useAddToCartWithLuxe();
  const { throwReport } = useALError();

  const [igProductPrices, setIgProductPrices] = useState({});

  const { open = false, handles = [], title: popupTitle = "" } = recommendedProductsPopupData || {};
  const { data: products, isInitialLoading: isLoading } = useProductData2(
    handles,
    "long",
    countryCode
  );
  const availableProducts = products.filter((p) => !isOutOfStock(p.shopify, countryCode));

  const showAddAllToCart =
    availableProducts?.length > 0 &&
    availableProducts.every((product) => product.shopify.variants?.length === 1);

  const onClosePopup = () => {
    setRecommendedProductsPopupData({
      open: false,
      handles: [],
      title: "",
    });
    trackRecommendedProductsPopupAction({ type: "close" });
  };

  const prepProductForATC = useCallback(
    (product) => {
      const igPrices = igProductPrices[product.handle] || {};

      const {
        shopify: { shopifyId, title, handle, productType, images, variants },
      } = product;

      return {
        quantity: 1,
        attributes: [],
        variant: {
          id: variants[0].id,
          sku: variants[0].sku,
          price: igPrices.finalPrice,
          compareAtPrice: igPrices.compareAtPrice,
          product: {
            title,
            id: shopifyId,
            handle,
            productType,
            images,
          },
        },
        attributesProperties: {
          isUS: countryCode === Country.US,
          currencyCode,
          prices: igPrices,
          isFinalSale: product.shopify.tags.includes(TAG.FINAL_SALE),
          discountsInfo,
          igPrices,
        },
      };
    },
    [countryCode, currencyCode, discountsInfo, igProductPrices]
  );

  const onAddAllToCart = useCallback(async () => {
    try {
      const dataProductsToAdd = [];
      availableProducts.forEach((product) => {
        const dataProductToAdd = prepProductForATC(product);
        dataProductsToAdd.push(dataProductToAdd);
      });
      await addToCartWithLuxe({ dataProductsToAdd });
      trackRecommendedProductsATC({
        type: "add_all_to_cart",
        handles: availableProducts.map((p) => p.handle),
      });
    } catch (error) {
      console.error(error);
      throwReport(error);
    }
  }, [
    addToCartWithLuxe,
    availableProducts,
    prepProductForATC,
    trackRecommendedProductsATC,
    throwReport,
  ]);

  return (
    <div className="product_recommendations__overlay" style={{ zIndex: isSideCartOpen ? 1 : 9999 }}>
      <div
        onClick={onClosePopup}
        className={`backdrop ${open ? "backdrop-visible" : "backdrop-closed"}`}
      />

      <div className={`product_recommendations__content ${open ? "open" : "closed"}`}>
        {isLoading && <ALLoading />}
        {!isLoading && (
          <>
            <div className="product_recommendations__header">
              <h3 className="h3 h3--bold product_recommendations__title">{popupTitle}</h3>
              <button type="button" onClick={onClosePopup} data-testid="close-popup">
                <IconCloseRound fill="none" stroke="#2D2927" width="30px" alt="close" />
              </button>
            </div>
            <div className="product_recommendations__products">
              {availableProducts.length > 0 &&
                availableProducts.map((product) => (
                  <ProductElement
                    key={product.id}
                    product={product}
                    componentType={ComponentType.PRODUCT_RECOMMENDATIONS}
                    igProductPrices={igProductPrices}
                    setIgProductPrices={setIgProductPrices}
                  />
                ))}
            </div>
            {showAddAllToCart && (
              <div className="product_recommendations__atc">
                <ALButton onClick={onAddAllToCart}>ADD ALL TO CART</ALButton>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ProductRecommendationsPopup;
